import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { generateThemeVariables, isHexColor } from '@/utils/colorUtils';

export interface CustomTheme {
  id: string;
  name: string;
  primaryColor: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: number;
  radius: number;
}

export type Theme = 'default' | 'neo-brutalist' | 'luxury-dark' | 'playful-pastel' | string;
export type Mode = 'dark' | 'light' | 'system';

interface ThemeProviderState {
  theme: Theme;
  mode: Mode;
  customThemes: CustomTheme[];
  setTheme: (theme: Theme) => void;
  setMode: (mode: Mode) => void;
  addCustomTheme: (theme: CustomTheme) => void;
  removeCustomTheme: (id: string) => void;
}

const initialState: ThemeProviderState = {
  theme: 'default',
  mode: 'system',
  customThemes: [],
  setTheme: () => null,
  setMode: () => null,
  addCustomTheme: () => null,
  removeCustomTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Theme fields are persisted by the user's browser (localStorage JSON), so a
 * tampered value would otherwise be interpolated straight into the generated
 * stylesheet. Anything non-numeric or out of the builder's range falls back to
 * the default instead of reaching CSS.
 */
const toCssNumber = (value: unknown, fallback: number, max: number): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 && num <= max ? num : fallback;
};

const readStorage = (key: string) => { try { return localStorage.getItem(key); } catch { return null; } };

const BUILT_IN_THEMES = ['default', 'neo-brutalist', 'luxury-dark', 'playful-pastel'];
const isBuiltInTheme = (value: unknown): boolean => typeof value === 'string' && BUILT_IN_THEMES.includes(value);

export function ThemeProvider({
  children,
  defaultTheme = 'default',
  defaultMode = 'system',
  storageKey = 'vite-ui-theme',
  modeStorageKey = 'vite-ui-theme-mode',
  customThemesStorageKey = 'vite-ui-custom-themes',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultMode?: Mode;
  storageKey?: string;
  modeStorageKey?: string;
  customThemesStorageKey?: string;
}) {

  const [theme, setTheme] = useState<Theme>(() => {
    // localStorage is user-editable: only accept a value we can act on.
    const stored = readStorage(storageKey);
    return isBuiltInTheme(stored) || (typeof stored === 'string' && stored.startsWith('custom-'))
      ? (stored as Theme)
      : defaultTheme;
  });

  const [mode, setMode] = useState<Mode>(() => {
    const stored = readStorage(modeStorageKey);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : defaultMode;
  });

  const [customThemes, setCustomThemes] = useState<CustomTheme[]>(() => {
    try {
      const stored = localStorage.getItem(customThemesStorageKey);
      if (!stored) return [];
      const parsed: unknown = JSON.parse(stored);
      // Reject a non-array / malformed payload instead of letting the wrong
      // shape reach generateThemeVariables.
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (t): t is CustomTheme =>
          Boolean(t) && typeof t === 'object' && typeof (t as CustomTheme).id === 'string' && (t as CustomTheme).id.startsWith('custom-') && typeof (t as CustomTheme).name === 'string' && isHexColor((t as CustomTheme).primaryColor),
      );
    } catch {
      return [];
    }
  });

  // Resolve mode before generating variables, including OS changes in system mode.
  // The previous pair of effects generated colours using the previous root class.
  useEffect(() => {
    const root = document.documentElement;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = mode === 'dark' || (mode === 'system' && query.matches);
      root.classList.toggle('dark', dark);
      root.classList.toggle('light', !dark);
      const custom = customThemes.find(item => item.id === theme);
      const effective = isBuiltInTheme(theme) ? theme : custom ? 'custom' : 'default';
      if (effective === 'default') root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', effective);
      let style = document.getElementById('custom-theme-styles');
      if (effective !== 'custom' || !custom) { style?.remove(); return; }
      if (!style) { style = document.createElement('style'); style.id = 'custom-theme-styles'; document.head.appendChild(style); }
      const variables = generateThemeVariables(custom.primaryColor, toCssNumber(custom.radius, 0.5, 2), dark,
        custom.secondaryColor, custom.backgroundColor, custom.textColor, custom.borderColor,
        custom.borderWidth == null ? undefined : toCssNumber(custom.borderWidth, 1, 4));
      style.textContent = `[data-theme='custom'] {${Object.entries(variables).map(([key, value]) => `${key}: ${value};`).join('')}}`;
    };
    apply();
    if (mode === 'system') query.addEventListener('change', apply);
    return () => { query.removeEventListener('change', apply); };
  }, [theme, mode, customThemes]);

  const handleSetTheme = useCallback((newTheme: Theme) => {
    localStorage.setItem(storageKey, newTheme);
    setTheme(newTheme);
  }, [storageKey]);

  const handleSetMode = useCallback((newMode: Mode) => {
    localStorage.setItem(modeStorageKey, newMode);
    setMode(newMode);
  }, [modeStorageKey]);

  const handleAddCustomTheme = useCallback((newTheme: CustomTheme) => {
    const next = [...customThemes.filter(item => item.id !== newTheme.id), newTheme];
    localStorage.setItem(customThemesStorageKey, JSON.stringify(next));
    setCustomThemes(next);
  }, [customThemesStorageKey, customThemes]);

  const handleRemoveCustomTheme = useCallback((id: string) => {
    const next = customThemes.filter(item => item.id !== id);
    localStorage.setItem(customThemesStorageKey, JSON.stringify(next));
    setCustomThemes(next);
    if (theme === id) {
      setTheme('default');
      localStorage.setItem(storageKey, 'default');
    }
  }, [customThemesStorageKey, storageKey, theme, customThemes]);

  const value = useMemo(() => ({
    theme,
    mode,
    customThemes,
    setTheme: handleSetTheme,
    setMode: handleSetMode,
    addCustomTheme: handleAddCustomTheme,
    removeCustomTheme: handleRemoveCustomTheme,
  }), [theme, mode, customThemes, handleSetTheme, handleSetMode, handleAddCustomTheme, handleRemoveCustomTheme]);

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
